import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import React from 'react';
import Button from '../components/ui/Button';
import ListPagination from '../components/ui/ListPagination';
import { SHOULD_RUN_GOOGLE_OPTIMIZE } from '../constants';
import { useGlobalState } from '../state/globalStateContext';
import * as styles from './hooks.module.scss';
import { sortBy } from './nodash';

export function useActivateGoogleOptimize(): boolean {
  const [isHidden, setIsHidden] = useState(SHOULD_RUN_GOOGLE_OPTIMIZE);
  // Initialize google optimize experiment on 'optimize.activate'
  useEffect(() => {
    if (SHOULD_RUN_GOOGLE_OPTIMIZE) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'optimize.activate' });
      // Send analytics.activate event with delay to ensure that Google Optimize
      // has had time to run the experiment before analytics triggers
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        window.dataLayer!.push({ event: 'analytics.activate' });
      }, 100);
      setIsHidden(false);
    }
  }, []);
  return isHidden;
}

export function useStoreUTMParamsOnLoad(): void {
  const {
    setUtmSource,
    setUtmMedium,
    setUtmCampaign,
    setUtmTerm,
    setUtmContent,
    setUtmDevice,
    setUtmTarget,
  } = useGlobalState();
  useEffect(() => {
    const utmParamNamesToSetFunction = {
      utm_source: setUtmSource,
      utm_medium: setUtmMedium,
      utm_campaign: setUtmCampaign,
      utm_term: setUtmTerm,
      utm_content: setUtmContent,
      utm_device: setUtmDevice,
      utm_target: setUtmTarget,
    } as const;
    const utmParamNames = Object.keys(utmParamNamesToSetFunction) as Array<
      keyof typeof utmParamNamesToSetFunction
    >;

    const urlSearchParams = new URLSearchParams(window.location.search);

    const urlHasUtmParams =
      utmParamNames.map(utmParamName => urlSearchParams.get(utmParamName)).filter(Boolean).length >
      0;

    /* eslint-disable @typescript-eslint/no-unnecessary-condition */
    if (urlHasUtmParams) {
      for (const utmParamName of utmParamNames) {
        const utmParamValue = urlSearchParams.get(utmParamName);
        if (utmParamValue) {
          // store in global state
          utmParamNamesToSetFunction[utmParamName](utmParamValue);
          if ('localStorage' in window && window.localStorage) {
            // store in local storage
            window.localStorage.setItem(utmParamName, utmParamValue);
          }
        } else if ('localStorage' in window && window.localStorage) {
          // remove from local storage
          window.localStorage.removeItem(utmParamName);
        }
      }
    } else if ('localStorage' in window && window.localStorage) {
      // get from local storage to global state
      setUtmSource(window.localStorage.getItem('utm_source'));
      setUtmMedium(window.localStorage.getItem('utm_medium'));
      setUtmCampaign(window.localStorage.getItem('utm_campaign'));
      setUtmTerm(window.localStorage.getItem('utm_term'));
      setUtmContent(window.localStorage.getItem('utm_content'));
      setUtmDevice(window.localStorage.getItem('utm_device'));
      setUtmTarget(window.localStorage.getItem('utm_target'));
    }
    /* eslint-enable @typescript-eslint/no-unnecessary-condition */
  }, []);
}

export function useStoreReferrerOnLoad(): void {
  const { setWebsiteReferrer } = useGlobalState();
  useEffect(() => {
    const websiteReferrer = document.referrer;
    const websiteReferrerHost = websiteReferrer ? new URL(websiteReferrer).host : null;
    const isReferrerCurrentWebsite = websiteReferrerHost === window.location.host;
    /* eslint-disable @typescript-eslint/no-unnecessary-condition */
    if (websiteReferrer && !isReferrerCurrentWebsite) {
      // store in global state
      setWebsiteReferrer(websiteReferrer);
      if ('localStorage' in window && window.localStorage) {
        // store in local storage
        window.localStorage.setItem('website_referrer', websiteReferrer);
      }
    } else if ('localStorage' in window && window.localStorage) {
      // get from local storage to global state
      setWebsiteReferrer(window.localStorage.getItem('website_referrer'));
    }
    /* eslint-enable @typescript-eslint/no-unnecessary-condition */
  }, []);
}

export interface TrackingData {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
  utmDevice: string | null;
  utmTarget: string | null;
  websiteReferrer: string | null;
}

export function useTrackingData(): TrackingData {
  const {
    utmSource,
    utmMedium,
    utmCampaign,
    utmTerm,
    utmContent,
    utmDevice,
    utmTarget,
    websiteReferrer,
  } = useGlobalState();
  return {
    utmSource,
    utmMedium,
    utmCampaign,
    utmTerm,
    utmContent,
    utmDevice,
    utmTarget,
    websiteReferrer,
  };
}

export function useSingleRunCallback(callBack: () => void): void {
  const hasBeenCalled = useRef(false);
  if (hasBeenCalled.current) return;
  callBack();
  hasBeenCalled.current = true;
}

export interface ElementDimensions {
  width: number;
  height: number;
  top: number;
  left: number;
  x: number;
  y: number;
  right: number;
  bottom: number;
}

function getDimensionsObject(node: HTMLElement): ElementDimensions {
  const rect = node.getBoundingClientRect();

  return {
    width: rect.width,
    height: rect.height,
    // @ts-expect-error
    top: 'x' in rect ? rect.x : rect.top,
    // @ts-expect-error
    left: 'y' in rect ? rect.y : rect.left,
    // @ts-expect-error
    x: 'x' in rect ? rect.x : rect.left,
    // @ts-expect-error
    y: 'y' in rect ? rect.y : rect.top,
    right: rect.right,
    bottom: rect.bottom,
  };
}
export interface UseDimensionsOptions {
  measureOnResize?: boolean;
  measureOnScroll?: boolean;
}

export function useDimensions(
  ref: React.MutableRefObject<HTMLElement | null>,
  { measureOnResize = true, measureOnScroll = true }: UseDimensionsOptions = {},
): ElementDimensions | null {
  const [dimensions, setDimensions] = useState<ElementDimensions | null>(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (node) {
      const measure = () =>
        window.requestAnimationFrame(() => {
          setDimensions(getDimensionsObject(node));
        });
      measure();

      if (measureOnResize) {
        window.addEventListener('resize', measure);
      }
      if (measureOnScroll) {
        window.addEventListener('scroll', measure);
      }

      return () => {
        if (measureOnResize) {
          window.removeEventListener('resize', measure);
        }
        if (measureOnScroll) {
          window.removeEventListener('scroll', measure);
        }
      };
    }
    return;
  }, [ref.current]);

  return dimensions;
}

export interface WindowDimensions {
  width: number;
  height: number;
}

/**
 * Returns an object with the width and height of the window if it's being used in the browser,
 * otherwise returns null
 */
export function useWindowDimensions(): WindowDimensions | null {
  const [dimensions, setDimensions] = useState<WindowDimensions | null>(null);
  useLayoutEffect(() => {
    function handleResize() {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    }

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return dimensions;
}

export function useScrollY(): number | null {
  const [scrollY, setScrollY] = useState<number | null>(null);
  useLayoutEffect(() => {
    const getScrollY = () =>
      window.requestAnimationFrame(() => {
        setScrollY(window.scrollY);
      });
    getScrollY();
    window.addEventListener('scroll', getScrollY);
    return () => {
      window.removeEventListener('scroll', getScrollY);
    };
  }, []);

  return scrollY;
}

export function useOnLoadEffect(effectFunction: () => void): boolean {
  const [hasRunOnLoadEffect, setHasRunOnLoadEffect] = useState(false);
  useEffect(() => {
    effectFunction();
    setHasRunOnLoadEffect(true);
  }, []);
  return hasRunOnLoadEffect;
}

export function usePagination<Item>(
  items: Array<Item>,
  itemsPerPage: number,
): {
  currentPage: number;
  currentPageItems: Item[];
  getPageUrlPart: () => readonly [string, string | null];
  resetPagination: () => void;
  renderPagination: () => JSX.Element;
} {
  const [currentPage, setCurrentPage] = useState(1);
  const [nCardsVisible, setNCardsVisible] = useState(itemsPerPage);

  const currentPageItems = items.slice(
    (currentPage - 1) * nCardsVisible,
    currentPage * nCardsVisible,
  );

  const hasRunOnLoadEffect = useOnLoadEffect(() => {
    let initialPage = 1;
    const currentUrl = new URLSearchParams(window.location.search);
    const urlPageStr = currentUrl.get('page');
    if (urlPageStr && urlPageStr.match(/^\d+$/)) {
      const urlPage = parseInt(urlPageStr);
      const totalPages = Math.ceil(items.length / itemsPerPage);
      if (1 <= urlPage && urlPage <= totalPages) {
        initialPage = urlPage;
      }
    }
    setCurrentPage(initialPage || 1);
  });

  function getPageUrlPart(): readonly [string, string | null] {
    const totalPages = Math.ceil(items.length / itemsPerPage);
    const page = totalPages > 1 ? currentPage + '' : null;
    return ['page', page];
  }

  const resetPagination = () => {
    setCurrentPage(1);
    setNCardsVisible(itemsPerPage);
  };

  const renderPagination = () => (
    <>
      {hasRunOnLoadEffect && nCardsVisible < items.length && (
        <Button
          outlined
          onClick={() => {
            setNCardsVisible(nCardsVisible + itemsPerPage);
          }}
          className={styles.listPaginationMobileButton}
        >
          Load more
        </Button>
      )}
      {hasRunOnLoadEffect && items.length > itemsPerPage && (
        <ListPagination
          currentPage={currentPage}
          totalItems={items.length}
          itemsPerPage={nCardsVisible}
          onPrevPageClick={() => {
            setCurrentPage(currentPage - 1);
          }}
          onNextPageClick={() => {
            setCurrentPage(currentPage + 1);
          }}
          onPageClick={(pageNumber: number) => setCurrentPage(pageNumber)}
        ></ListPagination>
      )}
    </>
  );

  return {
    currentPage,
    currentPageItems,
    getPageUrlPart,
    resetPagination,
    renderPagination,
  };
}

export function useUpdateUrlFromFilters(
  getUrlParts: () => Array<readonly [string, string | null]>,
  dependencies: Array<unknown>,
): void {
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    for (const [key, value] of getUrlParts()) {
      if (value) {
        urlSearchParams.set(key, value);
      }
    }

    const newQuery = urlSearchParams.toString();
    const newUrlPathAndQuery = window.location.pathname + (newQuery ? '?' + newQuery : '');
    history.replaceState(null, '', newUrlPathAndQuery);
  }, dependencies);
}

export function useAnimatedTypewritingTextsInfo(
  sentencesArrays: Array<Array<string>>,
  {
    transitionDuration = 750,
    transitionPeriod = 2000,
  }: { transitionDuration?: number; transitionPeriod?: number } = {},
): { currentSentenceIndex: number; nVisibleCharsArray: Array<number> } {
  if (
    !sentencesArrays
      .map(sentences => sentences.length)
      .every(sentencesLength => sentencesLength === sentencesArrays[0].length)
  ) {
    throw new Error('Not every sentencesArray has the same length');
  }
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [nVisibleCharsArray, setNVisibleCharsArray] = useState(
    sentencesArrays.map(sentences => sentences[0].length),
  );
  const [currentState, setCurrentState] = useState<'shrinking' | 'growing' | 'waiting'>('waiting');

  const sentenceTransitionsList = useRef<
    Array<{
      sentencesIndex: number;
      transitionTime: number;
      transitionDuration: number;
    }>
  >([]);

  useEffect(() => {
    if (currentState === 'waiting') {
      const timeout = setTimeout(() => {
        setCurrentState('shrinking');
      }, transitionPeriod);
      return () => {
        clearTimeout(timeout);
      };
    }

    if (sentenceTransitionsList.current.length === 0) {
      const currentSentences = sentencesArrays.map(sentences => sentences[currentSentenceIndex]);
      for (let i = 0; i < currentSentences.length; i++) {
        const sentence = currentSentences[i];

        const charTransitionDuration = transitionDuration / 2 / sentence.length;

        for (let j = 0; j < sentence.length; j++) {
          sentenceTransitionsList.current.push({
            sentencesIndex: i,
            transitionTime: j * charTransitionDuration,
            transitionDuration: charTransitionDuration,
          });
        }
      }
      sentenceTransitionsList.current = sortBy(
        sentenceTransitionsList.current,
        ({ transitionTime }) => transitionTime,
        'desc',
      );

      for (let i = sentenceTransitionsList.current.length - 1; i >= 0; i--) {
        const transition = sentenceTransitionsList.current[i];
        transition.transitionDuration =
          i === sentenceTransitionsList.current.length - 1
            ? 0
            : transition.transitionTime - sentenceTransitionsList.current[i + 1].transitionTime;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const sentenceTransition = sentenceTransitionsList.current.pop()!;

    const timeout = setTimeout(() => {
      function setCurrentNVisibleChars(nVisibleChars: number) {
        const newNVisibleCharsArray = [...nVisibleCharsArray];
        newNVisibleCharsArray[sentenceTransition.sentencesIndex] = nVisibleChars;
        setNVisibleCharsArray(newNVisibleCharsArray);
      }

      if (currentState === 'shrinking') {
        setCurrentNVisibleChars(nVisibleCharsArray[sentenceTransition.sentencesIndex] - 1);
        if (sentenceTransitionsList.current.length === 0) {
          const newSentenceIndex = (currentSentenceIndex + 1) % sentencesArrays[0].length;
          setCurrentSentenceIndex(newSentenceIndex);
          setCurrentState('growing');
        }
      }
      if (currentState === 'growing') {
        setCurrentNVisibleChars(nVisibleCharsArray[sentenceTransition.sentencesIndex] + 1);
        if (sentenceTransitionsList.current.length === 0) {
          setCurrentState('waiting');
        }
      }
    }, sentenceTransition.transitionDuration);
    return () => {
      clearTimeout(timeout);
    };
  }, [...nVisibleCharsArray, currentState]);
  return {
    currentSentenceIndex,
    nVisibleCharsArray,
  };
}

/**
 * Allows using intersection observer.
 * Can be used to detect full or partial visibility, or any other intersection logic needed.
 * @param options
 * @param options.threshold Either a single number or an array of numbers between 0 and 1 (the default), indicating at what percentage of the target’s visibility the observer’s callback should be executed. Defaults to 1.
 * @param options.root The Element that is used as the viewport for checking visibility of the target. Defaults to the browser viewport if not specified or if null.
 * @param options.rootMargin Margin around the root. Can have values similar to the CSS margin property. The values can be percentages. This set of values serves to grow or shrink each side of the root element’s bounding box before computing intersections. Defaults to all zeros.
 */
export function useIntersectionObserver(
  options: { threshold?: number; root?: Element | Document | null; rootMargin?: string } = {},
): [ref: (node: HTMLElement | null) => void, entry: IntersectionObserverEntry | null] {
  const { threshold = 1, root = null, rootMargin = '0px' } = options;
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);

  const previousObserver = useRef<IntersectionObserver | null>(null);

  const customRef = useCallback(
    (node: HTMLElement | null) => {
      if (previousObserver.current) {
        previousObserver.current.disconnect();
        previousObserver.current = null;
      }

      if (node?.nodeType === Node.ELEMENT_NODE) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            setEntry(entry);
          },
          { threshold, root, rootMargin },
        );

        observer.observe(node);
        previousObserver.current = observer;
      }
    },
    [threshold, root, rootMargin],
  );

  return [customRef, entry];
}

/**
 * One-shot viewport detector built on top of `useIntersectionObserver`.
 * Returns `true` the first time the observed element crosses the
 * intersection threshold, and stays `true` afterwards. Useful for
 * deferring expensive work (e.g. eager-loading images inside scrolling
 * carousels where native lazy loading doesn't track transform-moved
 * children reliably) until the module is actually near the viewport.
 */
export function useHasEnteredViewport(
  options: { rootMargin?: string; threshold?: number } = {},
): [ref: (node: HTMLElement | null) => void, hasEntered: boolean] {
  const { rootMargin = '500px', threshold = 0 } = options;
  const [ref, entry] = useIntersectionObserver({ threshold, rootMargin });
  const [hasEntered, setHasEntered] = useState(false);
  useEffect(() => {
    if (entry?.isIntersecting) setHasEntered(true);
  }, [entry]);
  return [ref, hasEntered];
}

/**
 * Provides a way to reactively observe DOM mutations and identify elements using a provided CSS selector.
 * It listens for changes in the DOM that result in an element matching the specified selector becoming available.
 *
 * @param {Object} options - Configuration options for the DOM observer.
 * @param {string} options.selector - The CSS selector string used to match the element within the root's descendants.
 * @param {HTMLElement} [options.root] - The root element within which to look for the matched element. Defaults to `document.body`.
 * @returns {HTMLElement | null} The first element within the root that matches the selector, or `null` if no such element is present.
 *
 * @example
 * // To observe for an element with id "target-element" within the document body:
 * const targetElement = useDomObserver({ selector: '#target-element' });
 */
export function useDomObserver({ selector, root }: { selector: string; root?: HTMLElement }) {
  const [matchedElement, setMatchedElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const queryRoot = root || document.body;
    const observer = new MutationObserver(mutationsList => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const el = queryRoot.querySelector<HTMLElement>(selector);
          if (el) {
            setMatchedElement(el);
            return;
          }
        }
      }
      setMatchedElement(null);
    });

    observer.observe(queryRoot, {
      childList: true,
      subtree: true,
    });

    // Initial check in case the element is already in the DOM
    const el = queryRoot.querySelector<HTMLElement>(selector);
    if (el) {
      setMatchedElement(el);
    }

    return () => {
      observer.disconnect();
    };
  }, [selector, root]);

  return matchedElement;
}

/**
 * Hook for persisting state to local or session storage.
 *
 * This hook manages a piece of state that is synchronized with the chosen storage,
 * ensuring that the state remains persistent across page reloads. When the state
 * is set, it is also saved to the chosen storage.
 *
 * @param key - The key under which to store the value in the chosen storage.
 * @param initialValue - The initial value to use if there is nothing stored under the key yet.
 * @param storageType - The chosen storage, defaults to local.
 * @returns A tuple containing:
 *           1. The current state value
 *           2. A function to update this value and write to the chosen storage.
 */

type StorageType = 'local' | 'session';

export function usePersistentState<T extends string | number | boolean | null | undefined>(
  key: string,
  initialValue: T,
  storageType: StorageType = 'local',
): [T, (newValue: T) => void] {
  const storage =
    typeof window !== 'undefined'
      ? storageType === 'local'
        ? window.localStorage
        : window.sessionStorage
      : null;

  function getParsedValueFromStorage(): T | null {
    const storedValue = storage?.getItem(key) || null;
    return (storedValue != null && (JSON.parse(storedValue) as T)) || null;
  }

  const [value, setValue] = useState<T>(getParsedValueFromStorage() || initialValue);

  function setPersistentValue(newValue: T) {
    setValue(newValue);
    if (storage) {
      storage.setItem(key, JSON.stringify(newValue));
    }
  }

  // Effect hook to initialize the state from storage on mount
  useEffect(() => {
    if (storage) {
      const parsedValue = getParsedValueFromStorage();
      if (parsedValue !== null) {
        setValue(parsedValue);
      }
    }
  }, [key, storage]);

  return [value, setPersistentValue];
}
